import React, { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAdminAuth } from '@/context/AdminAuthContext';

export default function AdminLogin() {
  const { login, verifying, error } = useAdminAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    await login(email, password);
  };

  return (
    <div className="relative min-h-screen overflow-hidden flex items-center justify-center px-4 py-10">
      {/* Soft brand atmosphere */}
      <div
        className="absolute inset-0"
        style={{
          background:
            'radial-gradient(ellipse 80% 60% at 15% 20%, rgba(8,136,248,0.14), transparent 55%), radial-gradient(ellipse 70% 50% at 90% 80%, rgba(248,176,16,0.16), transparent 50%), linear-gradient(165deg, #F4F8FC 0%, #FFFFFF 45%, #EEF4FA 100%)',
        }}
      />
      <div
        className="absolute inset-0 opacity-[0.35] pointer-events-none"
        style={{
          backgroundImage:
            'radial-gradient(circle at 1px 1px, rgba(120,160,208,0.35) 1px, transparent 0)',
          backgroundSize: '28px 28px',
        }}
      />

      <div className="relative w-full max-w-[400px] animate-in fade-in duration-500">
        <div className="text-center mb-8">
          <img
            src="/logo.png"
            alt="CardX Academia"
            className="h-14 mx-auto object-contain mb-5 drop-shadow-sm"
          />
          <h1
            className="text-[1.75rem] font-semibold tracking-tight text-gray-900"
            style={{ fontFamily: '"Playfair Display", Georgia, serif' }}
          >
            Staff portal
          </h1>
          <p className="text-sm text-[#5A6B7D] mt-1.5 leading-relaxed">
            Sign in to manage appointments, registrations, and requests.
          </p>
        </div>

        <form
          onSubmit={handleSubmit}
          className="rounded-2xl bg-white/80 backdrop-blur-sm px-7 py-7 shadow-[0_1px_0_rgba(8,136,248,0.06),0_12px_40px_-16px_rgba(8,136,248,0.18)] ring-1 ring-[#0888F8]/10 space-y-5"
        >
          <div>
            <Label htmlFor="admin-email" className="text-[#3D4F63]">
              Email
            </Label>
            <Input
              id="admin-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@cardxacademia.com"
              className="mt-1.5 h-11 border-[#D5E3F0] bg-white/90 focus-visible:ring-[#0888F8]/30 focus-visible:border-[#0888F8]"
              autoComplete="username"
              autoFocus
              required
            />
          </div>
          <div>
            <Label htmlFor="admin-password" className="text-[#3D4F63]">
              Password
            </Label>
            <Input
              id="admin-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••••••"
              className="mt-1.5 h-11 border-[#D5E3F0] bg-white/90 focus-visible:ring-[#0888F8]/30 focus-visible:border-[#0888F8]"
              autoComplete="current-password"
              required
            />
          </div>

          {error && (
            <p className="text-sm text-red-600 bg-red-50/90 rounded-lg px-3 py-2.5">
              {error}
            </p>
          )}

          <Button
            type="submit"
            disabled={verifying || !email.trim() || !password}
            className="w-full h-11 bg-[#0888F8] hover:bg-[#0670D4] text-white font-semibold shadow-sm shadow-[#0888F8]/25 transition-all duration-200 hover:shadow-md hover:shadow-[#0888F8]/30"
          >
            {verifying ? (
              <>
                <Loader2 size={18} className="mr-2 animate-spin" />
                Signing in…
              </>
            ) : (
              'Sign in'
            )}
          </Button>
        </form>

        <p className="text-center text-xs text-[#8A9BB0] mt-6">
          CardX Academia · Staff access
        </p>
      </div>
    </div>
  );
}
